import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import Logo from "../../assets/img/logo.png";
import { API_URL } from "../../config";
import {
  FiSearch,
  FiUser,
  FiPhone,
  FiMail,
  FiMapPin,
  FiCalendar,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiMessageSquare,
  FiAward,
  FiHome,
  FiBriefcase,
  FiFileText,
  FiImage,
  FiTarget,
  FiFlag,
  FiPhoneCall,
  FiCheck,
  FiX,
} from "react-icons/fi";

const VisitorSearch = () => {
  const [trackingId, setTrackingId] = useState("");
  const [visitorData, setVisitorData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeRemarkImages, setActiveRemarkImages] = useState({});
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlTrackingId = params.get("trackingId");

    if (urlTrackingId) {
      setTrackingId(urlTrackingId);
      fetchVisitorData(urlTrackingId);
    }
  }, [location.search]);

  const fetchVisitorData = async (id) => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API_URL}fatchdataByTrakingId`, {
        traking_id: id,
      });

      if (response.data.status === "success" && response.data.data.length > 0) {
        setVisitorData(response.data.data[0]);
      } else {
        setVisitorData(null);
        setError("No data found for the provided Tracking ID.");
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred while fetching the data.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!trackingId.trim()) {
      setError("Please enter a valid Tracking ID.");
      return;
    }
    fetchVisitorData(trackingId);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const toggleRemarkImages = (remarkId) => {
    setActiveRemarkImages(prev => ({
      ...prev,
      [remarkId]: !prev[remarkId]
    }));
  };

  const DetailItem = ({ title, value, icon: Icon, highlight, className = "", badge }) => (
    <div className={`bg-gradient-to-br from-white to-emerald-50 p-3 sm:p-4 rounded-lg sm:rounded-xl shadow-sm border border-emerald-100 hover:shadow-md transition-all duration-300 ${className}`}>
      <div className="flex items-start gap-2 sm:gap-3">
        {Icon && (
          <div className="p-1.5 sm:p-2 bg-emerald-100 rounded-lg flex-shrink-0">
            <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-700" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-xs sm:text-sm font-medium text-emerald-700 truncate">{title}</h4>
            {badge && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-800">
                {badge}
              </span>
            )}
          </div>
          <p className={`${highlight ? highlight : "text-gray-800"} font-medium text-sm sm:text-base break-words`}>
            {value || "N/A"}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 p-3 sm:p-4 md:p-6">
      {/* Header Section */}
      <div className="max-w-6xl mx-auto mb-6 sm:mb-8">
        <div className="flex flex-col items-center text-center mb-6 sm:mb-8">
          <img
            src={Logo}
            alt="Logo"
            className="w-32 h-16 sm:w-48 sm:h-24 object-contain mb-4 sm:mb-6"
          />
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-emerald-900 mb-2 px-2">
            Visitor Tracking System
          </h1>
          <p className="text-emerald-700 max-w-md text-sm sm:text-base px-2">
            Enter your tracking ID to view visitor details and status
          </p>
        </div>

        {/* Search Card */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-emerald-200">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-500" />
            </div>
            <input
              type="text"
              value={trackingId}
              onChange={(e) => setTrackingId(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter your Tracking ID"
              className="block w-full pl-9 sm:pl-10 pr-3 py-3 sm:py-4 border border-emerald-200 rounded-lg sm:rounded-xl text-sm sm:text-base text-gray-700 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
            />
            <button
              onClick={handleSearch}
              disabled={loading}
              className="absolute right-1.5 top-1.5 sm:right-2 sm:top-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-md sm:rounded-lg font-semibold hover:from-emerald-700 hover:to-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base shadow-sm hover:shadow-md"
            >
              {loading ? "Searching..." : "Search"}
            </button>
          </div>

          {error && (
            <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-lg flex items-center">
              <FiXCircle className="mr-2 sm:mr-3 flex-shrink-0 h-4 w-4 sm:h-6 sm:w-6 text-red-500" />
              <span className="text-sm sm:text-base">{error}</span>
            </div>
          )}
        </div>
      </div>

      {/* Visitor Details Section */}
      {visitorData && (
        <div className="max-w-6xl mx-auto">
          {/* Office Name Banner */}
          {visitorData.office && (
            <div className="bg-gradient-to-r from-emerald-800 to-green-900 rounded-xl sm:rounded-2xl shadow-lg mb-4 sm:mb-6 overflow-hidden">
              <div className="p-3 sm:p-4 text-white">
                <div className="flex items-center justify-center">
                  <FiBriefcase className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" />
                  <div className="text-center">
                    <h2 className="text-sm sm:text-base md:text-lg font-bold">
                      {visitorData.office.name}
                    </h2>
                    <div className="flex flex-wrap items-center justify-center mt-1 gap-2">
                      <p className="text-emerald-200 text-xs sm:text-sm">
                        {visitorData.office.address}
                      </p>
                      {visitorData.office.phone && (
                        <span className="text-emerald-200 text-xs sm:text-sm flex items-center">
                          <FiPhoneCall className="mr-1" /> {visitorData.office.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Profile Header */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl overflow-hidden mb-6 sm:mb-8 border border-emerald-200">
            <div className="bg-gradient-to-r from-emerald-600 to-green-700 p-4 sm:p-6 text-white">
              <div className="flex flex-col md:flex-row items-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 rounded-full flex items-center justify-center mr-0 md:mr-6 mb-4 md:mb-0 border-2 border-white/30">
                  <FiUser className="w-8 h-8 sm:w-10 sm:h-10" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-2">
                    <h2 className="text-lg sm:text-xl md:text-2xl font-bold">
                      {visitorData.salutation ? `${visitorData.salutation} ` : ""}
                      {visitorData.visitor_name}
                    </h2>
                    <div className="mt-2 md:mt-0">
                      <span className="px-3 py-1 bg-white/20 rounded-full text-xs sm:text-sm font-medium">
                        ID: {visitorData.visitor_id}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-3">
                    <div
                      className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                        visitorData.status === "open"
                          ? "bg-amber-500/20 text-amber-200 border border-amber-300/20"
                          : "bg-green-500/20 text-green-200 border border-green-300/20"
                      }`}
                    >
                      {visitorData.status === "open"
                        ? "Follow-up Pending"
                        : "Follow-up Completed"}
                    </div>
                    <div className="px-2 sm:px-3 py-1 bg-white/20 rounded-full text-xs sm:text-sm border border-white/20">
                      Tracking ID: {visitorData.traking_id}
                    </div>
                    {visitorData.category_text && (
                      <div className="px-2 sm:px-3 py-1 bg-emerald-500/30 rounded-full text-xs sm:text-sm">
                        {visitorData.category_text}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <DetailItem
                  title="Mobile Number"
                  value={visitorData.mobile_number}
                  icon={FiPhone}
                  badge="Primary"
                />
                <DetailItem
                  title="Email Address"
                  value={visitorData.email_id}
                  icon={FiMail}
                />
                <DetailItem
                  title="WhatsApp Number"
                  value={visitorData.whatsappNumber || "N/A"}
                  icon={FiPhone}
                  badge={visitorData.whatsapp_tick === 1 ? "Verified" : "Not Verified"}
                />
                <DetailItem
                  title="Date of Birth"
                  value={new Date(visitorData.dob).toLocaleDateString('en-IN')}
                  icon={FiCalendar}
                />
                <DetailItem
                  title="Visit Purpose"
                  value={visitorData.visit_purpose}
                  icon={FiTarget}
                  highlight={
                    visitorData.visit_purpose === "Problem" 
                      ? "text-amber-600" 
                      : "text-emerald-600"
                  }
                />
                <DetailItem
                  title="IGRS Number"
                  value={visitorData.igrs_number || "Not provided"}
                  icon={FiFileText}
                  highlight="text-blue-600"
                />
              </div>
            </div>
          </div>

          {/* Information Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
            {/* Location Details */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-emerald-100 p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-emerald-800 mb-3 sm:mb-4 flex items-center">
                <FiMapPin className="mr-2 text-emerald-600" /> Location Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <DetailItem 
                  title="State" 
                  value={visitorData.state} 
                  icon={FiFlag}
                />
                <DetailItem 
                  title="District" 
                  value={visitorData.district} 
                />
                <DetailItem 
                  title="Assembly" 
                  value={visitorData.assembly} 
                />
                <DetailItem 
                  title="Category" 
                  value={visitorData.category_text || "N/A"} 
                  highlight="text-emerald-700 font-bold"
                  badge={visitorData.categorie_id === 10 ? "VIP" : "Standard"}
                />
              </div>
              <div className="mt-4 p-3 sm:p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                <h4 className="text-xs sm:text-sm font-medium text-emerald-700 mb-2 flex items-center">
                  <FiHome className="mr-2" /> Complete Address
                </h4>
                <p className="text-gray-800 text-sm sm:text-base">{visitorData.address || "N/A"}</p>
              </div>
            </div>

            {/* Quick Status */}
            <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-emerald-800 mb-3 sm:mb-4 flex items-center">
                <FiAward className="mr-2 text-emerald-600" /> Status Overview
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <DetailItem 
                  title="To Meet" 
                  value={visitorData.to_meet} 
                  icon={FiUser}
                />
                <div className="p-3 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-emerald-700">Current Status</span>
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-2 ${visitorData.status === 'open' ? 'bg-amber-500' : 'bg-green-500'}`}></div>
                      <span className={`text-xs font-medium ${visitorData.status === 'open' ? 'text-amber-600' : 'text-green-600'}`}>
                        {visitorData.status === 'open' ? 'Active' : 'Resolved'}
                      </span>
                    </div>
                  </div>
                  <p className={`font-semibold text-sm ${visitorData.status === 'open' ? 'text-amber-700' : 'text-green-700'}`}>
                    {visitorData.status === 'on-going' ? 'Follow-up Pending' : 'Follow-up Completed'}
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-emerald-50 rounded-lg text-center">
                    <div className="text-xs text-emerald-600 mb-1">WhatsApp</div>
                    <div className="flex items-center justify-center">
                      {visitorData.whatsapp_tick === 1 ? (
                        <FiCheck className="text-green-600" />
                      ) : (
                        <FiX className="text-red-500" />
                      )}
                      <span className={`ml-1 text-sm font-medium ${visitorData.whatsapp_tick === 1 ? 'text-green-600' : 'text-red-500'}`}>
                        {visitorData.whatsapp_tick === 1 ? 'Verified' : 'Not Verified'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-emerald-50 rounded-lg text-center">
                    <div className="text-xs text-emerald-600 mb-1">Documents</div>
                    <div className="text-sm font-medium text-gray-700">
                      {visitorData.document_image ? 'Available' : 'Not Uploaded'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline Section */}
          <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-4 sm:p-6 mb-4 sm:mb-6">
            <h3 className="text-base sm:text-lg font-semibold text-emerald-800 mb-3 sm:mb-4 flex items-center">
              <FiClock className="mr-2 text-emerald-600" /> Timeline
            </h3>
            <div className="relative pl-6 sm:pl-8 pt-2">
              {/* Timeline line */}
              <div className="absolute left-3 sm:left-4 top-0 bottom-0 w-0.5 bg-emerald-200"></div>
              
              <div className="space-y-4">
                <div className="relative">
                  <div className="absolute left-[-26px] sm:left-[-32px] top-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-emerald-500 border-2 border-white shadow-sm"></div>
                  <div className="ml-2">
                    <h4 className="text-sm font-medium text-emerald-700">Registration</h4>
                    <p className="text-sm text-gray-600">
                      {new Date(visitorData.created_at).toLocaleString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>

                <div className="relative">
                  <div className="absolute left-[-26px] sm:left-[-32px] top-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-emerald-400 border-2 border-white shadow-sm"></div>
                  <div className="ml-2">
                    <h4 className="text-sm font-medium text-emerald-700">Last Updated</h4>
                    <p className="text-sm text-gray-600">
                      {new Date(visitorData.updated_at).toLocaleString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>

                {visitorData.problem_resolvedate && (
                  <div className="relative">
                    <div className="absolute left-[-26px] sm:left-[-32px] top-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-green-500 border-2 border-white shadow-sm"></div>
                    <div className="ml-2">
                      <h4 className="text-sm font-medium text-green-700">Problem Resolved</h4>
                      <p className="text-sm text-gray-600">
                        {new Date(visitorData.problem_resolvedate).toLocaleString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                )}

                <div className="relative">
                  <div className="absolute left-[-26px] sm:left-[-32px] top-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-emerald-300 border-2 border-white shadow-sm"></div>
                  <div className="ml-2">
                    <h4 className="text-sm font-medium text-emerald-700">Date of Birth</h4>
                    <p className="text-sm text-gray-600">
                      {new Date(visitorData.dob).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Remarks Section */}
          {visitorData.remarks && visitorData.remarks.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-4 sm:p-6 mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-emerald-800 mb-3 sm:mb-4 flex items-center">
                <FiMessageSquare className="mr-2 text-emerald-600" /> Remarks History
                <span className="ml-2 px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-medium">
                  {visitorData.remarks.length} {visitorData.remarks.length === 1 ? 'Remark' : 'Remarks'}
                </span>
              </h3>
              <div className="space-y-4">
                {visitorData.remarks.map((remark, index) => (
                  <div key={remark.id} className="border-l-4 border-emerald-500 pl-4 py-3 bg-emerald-50/50 rounded-r-lg">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-2">
                      <div className="flex items-center">
                        <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center mr-2">
                          <span className="text-xs font-medium text-emerald-700">{index + 1}</span>
                        </div>
                        <p className="text-sm sm:text-base font-medium text-emerald-800">
                          Remark
                        </p>
                      </div>
                      <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">
                        {new Date(remark.created_at).toLocaleString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-gray-700 text-sm sm:text-base mb-3 bg-white p-3 rounded-lg border border-emerald-100">
                      {remark.remark}
                    </p>
                    
                    {remark.images && remark.images.length > 0 && (
                      <div className="mt-3">
                        <button
                          onClick={() => toggleRemarkImages(remark.id)}
                          className="flex items-center text-emerald-600 hover:text-emerald-800 text-sm font-medium mb-2 px-3 py-1.5 bg-emerald-100 hover:bg-emerald-200 rounded-lg transition-colors"
                        >
                          <FiImage className="mr-2" />
                          {activeRemarkImages[remark.id] ? 'Hide' : 'Show'} Attachments ({remark.images.length})
                        </button>
                        
                        {activeRemarkImages[remark.id] && (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-2">
                            {remark.images.map((image, imgIndex) => (
                              <a
                                key={image.id}
                                href={image.image_path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block border border-emerald-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow hover:border-emerald-300"
                              >
                                <div className="relative aspect-square">
                                  <img
                                    src={image.image_path}
                                    alt={`Remark ${index + 1} - Image ${imgIndex + 1}`}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs py-1 px-2 text-center">
                                    Image {imgIndex + 1}
                                  </div>
                                </div>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Query Section */}
          <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-emerald-800 mb-3 sm:mb-4 flex items-center">
              <FiFileText className="mr-2 text-emerald-600" /> Query Details
            </h3>
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200">
                <h4 className="text-sm font-medium text-emerald-700 mb-3 flex items-center">
                  <FiTarget className="mr-2" /> Visitor Query
                </h4>
                <div className="bg-white p-4 rounded-lg border border-emerald-100">
                  <p className="text-gray-800 text-sm sm:text-base whitespace-pre-line leading-relaxed">
                    {visitorData.query || "No query provided."}
                  </p>
                </div>
              </div>
              
              {visitorData.out_date && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="text-sm font-medium text-amber-700 mb-2">Expected Resolution Date</h4>
                  <p className="text-amber-800 font-medium">
                    {new Date(visitorData.out_date).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitorSearch;